/**
 * Blog Listing Page Logic
 *
 * Handles client-side pagination, filtering, and search for blog posts
 */

import { DateFormatter } from "./utils/date-formatter";

// Type definitions
interface BlogPostMeta {
  title: string;
  date: string; // ISO 8601: "2026-02-16"
  author: string;
  category: "open-source" | "consulting" | "ai" | "product" | "case-study";
  tags: string[];
  excerpt: string;
  slug: string;
  description: string;
}

interface BlogIndex {
  posts: BlogPostMeta[];
  categories: Record<string, number>;
  tags: Record<string, number>;
}

// State
let allPosts: BlogPostMeta[] = [];
let filteredPosts: BlogPostMeta[] = [];
let currentPage = 1;
const POSTS_PER_PAGE = 10;

// DOM elements
let postsContainer: HTMLElement | null = null;
let noResultsContainer: HTMLElement | null = null;
let paginationPrev: HTMLButtonElement | null = null;
let paginationNext: HTMLButtonElement | null = null;
let paginationInfo: HTMLElement | null = null;
let filterSearch: HTMLInputElement | null = null;
let filterCategory: HTMLSelectElement | null = null;
let clearFiltersBtn: HTMLButtonElement | null = null;

/**
 * Initialize blog listing
 */
async function init() {
  // Get DOM elements
  postsContainer = document.getElementById("blog-posts");
  noResultsContainer = document.getElementById("no-results");
  paginationPrev = document.getElementById("pagination-prev") as HTMLButtonElement;
  paginationNext = document.getElementById("pagination-next") as HTMLButtonElement;
  paginationInfo = document.getElementById("pagination-info");
  filterSearch = document.getElementById("filter-search") as HTMLInputElement;
  filterCategory = document.getElementById("filter-category") as HTMLSelectElement;
  clearFiltersBtn = document.getElementById("clear-filters-btn") as HTMLButtonElement;

  if (!postsContainer) {
    console.error("Blog posts container not found");
    return;
  }

  // Load blog index
  try {
    const response = await fetch("./blog/blog-index.json");
    if (!response.ok) {
      throw new Error(`Failed to load blog index: ${response.statusText}`);
    }
    const data: BlogIndex = await response.json();
    allPosts = data.posts;
    filteredPosts = [...allPosts];

    // Populate category filter
    populateCategoryFilter(data.categories);

    // Set up event listeners
    setupEventListeners();

    // Render initial page
    renderPosts();
  } catch (error) {
    console.error("Error loading blog posts:", error);
    postsContainer.innerHTML = '<p class="error">Could not load blog posts. Please try again later.</p>';
  }
}

/**
 * Populate category filter dropdown
 */
function populateCategoryFilter(categories: Record<string, number>) {
  if (!filterCategory) return;

  // Keep "All categories" option
  const allOption = filterCategory.querySelector('option[value=""]');

  // Clear other options
  filterCategory.innerHTML = "";
  if (allOption) {
    filterCategory.appendChild(allOption);
  }

  // Add category options
  const categoryMap: Record<string, string> = {
    "open-source": "Open Source",
    "consulting": "Consulting",
    "ai": "AI",
    "product": "Product",
    "case-study": "Case Study",
  };

  for (const [category, count] of Object.entries(categories)) {
    const option = document.createElement("option");
    option.value = category;
    option.textContent = `${categoryMap[category] || category} (${count})`;
    filterCategory.appendChild(option);
  }
}

/**
 * Set up event listeners
 */
function setupEventListeners() {
  if (paginationPrev) {
    paginationPrev.addEventListener("click", () => {
      if (currentPage > 1) {
        currentPage--;
        renderPosts();
      }
    });
  }

  if (paginationNext) {
    paginationNext.addEventListener("click", () => {
      const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
      if (currentPage < totalPages) {
        currentPage++;
        renderPosts();
      }
    });
  }

  if (filterSearch) {
    filterSearch.addEventListener("input", () => {
      applyFilters();
    });
  }

  if (filterCategory) {
    filterCategory.addEventListener("change", () => {
      applyFilters();
    });
  }

  if (clearFiltersBtn) {
    clearFiltersBtn.addEventListener("click", () => {
      clearFilters();
    });
  }
}

/**
 * Clear all filters and reset view
 */
function clearFilters() {
  if (filterSearch) filterSearch.value = "";
  if (filterCategory) filterCategory.value = "";
  applyFilters();
}

/**
 * Apply filters and reset to page 1
 */
function applyFilters() {
  const searchTerm = filterSearch?.value.toLowerCase() || "";
  const selectedCategory = filterCategory?.value || "";

  filteredPosts = allPosts.filter((post) => {
    // Search filter (title, excerpt, tags, author)
    const matchesSearch =
      !searchTerm ||
      post.title.toLowerCase().includes(searchTerm) ||
      post.excerpt.toLowerCase().includes(searchTerm) ||
      post.author.toLowerCase().includes(searchTerm) ||
      post.tags.some((tag) => tag.toLowerCase().includes(searchTerm));

    // Category filter
    const matchesCategory = !selectedCategory || post.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  currentPage = 1;
  renderPosts();
}

/**
 * Render posts for current page
 */
function renderPosts() {
  if (!postsContainer || !noResultsContainer) return;

  // Calculate pagination
  const totalPages = Math.ceil(filteredPosts.length / POSTS_PER_PAGE);
  const startIdx = (currentPage - 1) * POSTS_PER_PAGE;
  const endIdx = startIdx + POSTS_PER_PAGE;
  const postsToShow = filteredPosts.slice(startIdx, endIdx);

  // Show/hide empty state
  if (filteredPosts.length === 0) {
    postsContainer.style.display = "none";
    noResultsContainer.style.display = "block";
  } else {
    postsContainer.style.display = "grid";
    noResultsContainer.style.display = "none";
    postsContainer.innerHTML = postsToShow.map((post) => renderPostCard(post)).join("");
  }

  // Update pagination controls
  updatePagination(totalPages);

  // Scroll to top
  window.scrollTo({ top: 0, behavior: "smooth" });
}

/**
 * Render a single post card
 */
function renderPostCard(post: BlogPostMeta): string {
  const formattedDate = DateFormatter.fullDate(post.date);
  const categoryLabel = getCategoryLabel(post.category);

  // Truncate excerpt if too long
  const truncatedExcerpt = post.excerpt.length > 150
    ? post.excerpt.substring(0, 150) + "..."
    : post.excerpt;

  return `
    <article class="post-card">
      <div class="post-card-meta">
        <time datetime="${post.date}">${formattedDate}</time>
        <span class="post-card-category">${categoryLabel}</span>
      </div>
      <h2><a href="./blog/${post.slug}.html">${post.title}</a></h2>
      <p class="post-card-excerpt">${truncatedExcerpt}</p>
      <div class="post-card-tags">
        ${post.tags.map((tag) => `<span class="tag">#${tag}</span>`).join("")}
      </div>
      <a href="./blog/${post.slug}.html" class="post-card-link">Read more</a>
    </article>
  `;
}

/**
 * Get category label
 */
function getCategoryLabel(category: string): string {
  const labels: Record<string, string> = {
    "open-source": "Open Source",
    consulting: "Consulting",
    ai: "AI",
    product: "Product",
    "case-study": "Case Study",
  };
  return labels[category] || category;
}

/**
 * Update pagination controls
 */
function updatePagination(totalPages: number) {
  if (paginationPrev) {
    paginationPrev.disabled = currentPage === 1;
  }

  if (paginationNext) {
    paginationNext.disabled = currentPage === totalPages || totalPages === 0;
  }

  if (paginationInfo) {
    if (totalPages === 0) {
      paginationInfo.textContent = "No posts";
    } else {
      paginationInfo.textContent = `Page ${currentPage} of ${totalPages}`;
    }
  }
}

/**
 * Setup sticky header scroll handler
 */
function setupStickyHeader() {
  const header = document.querySelector('header');
  if (!header) return;

  // Add blog-header class for sticky behavior
  header.classList.add('blog-header');

  window.addEventListener('scroll', () => {
    if (window.scrollY > 50) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }
  });
}

/**
 * Setup back to top button
 */
function setupBackToTop() {
  // Create back to top button if it doesn't exist
  let backToTop = document.getElementById('back-to-top') as HTMLButtonElement;
  if (!backToTop) {
    backToTop = document.createElement('button');
    backToTop.id = 'back-to-top';
    backToTop.className = 'back-to-top';
    backToTop.setAttribute('aria-label', 'Back to top');
    backToTop.innerHTML = '↑';
    document.body.appendChild(backToTop);
  }

  // Show/hide button based on scroll position
  window.addEventListener('scroll', () => {
    if (window.scrollY > 300) {
      backToTop.classList.add('visible');
    } else {
      backToTop.classList.remove('visible');
    }
  });

  // Scroll to top on click
  backToTop.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });
}

// Initialize on DOM load
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    init();
    setupStickyHeader();
    setupBackToTop();
  });
} else {
  init();
  setupStickyHeader();
  setupBackToTop();
}
